export const CHAT_PROTOCOL_VERSION = 1 as const;

export const CHAT_LIMITS = {
  messageCodePoints: 4_000,
  wireMessageCharacters: 20_000,
  messageIdCharacters: 64,
} as const;

export interface ChatPayloads {
  'chat.message': {
    readonly messageId: string;
    readonly sentAt: number;
    readonly text: string;
  };
  'chat.typing': {
    readonly active: boolean;
    readonly sentAt: number;
  };
}

export type ChatMessageType = keyof ChatPayloads;

export type ChatWireMessage<Type extends ChatMessageType = ChatMessageType> = {
  readonly v: typeof CHAT_PROTOCOL_VERSION;
  readonly type: Type;
  readonly payload: ChatPayloads[Type];
};

export type AnyChatWireMessage = {
  [Type in ChatMessageType]: ChatWireMessage<Type>;
}[ChatMessageType];

export type ChatParseResult =
  | { readonly ok: true; readonly message: AnyChatWireMessage }
  | { readonly ok: false; readonly reason: 'invalid' | 'too-large' | 'unsupported-version' };

const MESSAGE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_DATE_TIMESTAMP = 8_640_000_000_000_000;

export function createChatMessage<Type extends ChatMessageType>(
  type: Type,
  payload: ChatPayloads[Type],
): ChatWireMessage<Type> {
  return { v: CHAT_PROTOCOL_VERSION, type, payload };
}

export function parseChatMessage(input: string | unknown): ChatParseResult {
  if (typeof input === 'string' && input.length > CHAT_LIMITS.wireMessageCharacters) {
    return { ok: false, reason: 'too-large' };
  }

  let value: unknown = input;
  if (typeof input === 'string') {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }

  if (isRecord(value) && value.v !== CHAT_PROTOCOL_VERSION) {
    return { ok: false, reason: 'unsupported-version' };
  }
  if (!isExactRecord(value, ['v', 'type', 'payload']) || value.v !== CHAT_PROTOCOL_VERSION) {
    return { ok: false, reason: 'invalid' };
  }

  if (value.type === 'chat.message' && isChatTextPayload(value.payload)) {
    return { ok: true, message: value as unknown as ChatWireMessage<'chat.message'> };
  }
  if (value.type === 'chat.typing' && isChatTypingPayload(value.payload)) {
    return { ok: true, message: value as unknown as ChatWireMessage<'chat.typing'> };
  }
  return { ok: false, reason: 'invalid' };
}

export function countCodePoints(value: string): number {
  return [...value].length;
}

function isChatTextPayload(value: unknown): boolean {
  return (
    isExactRecord(value, ['messageId', 'sentAt', 'text']) &&
    typeof value.messageId === 'string' &&
    MESSAGE_ID.test(value.messageId) &&
    isTimestamp(value.sentAt) &&
    typeof value.text === 'string' &&
    value.text.length > 0 &&
    countCodePoints(value.text) <= CHAT_LIMITS.messageCodePoints
  );
}

function isChatTypingPayload(value: unknown): boolean {
  return (
    isExactRecord(value, ['active', 'sentAt']) &&
    typeof value.active === 'boolean' &&
    isTimestamp(value.sentAt)
  );
}

function isTimestamp(value: unknown): boolean {
  return Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= MAX_DATE_TIMESTAMP;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isExactRecord(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    Object.keys(value).length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}
